/**
 * 模型能力标签 — 统一渲染逻辑
 *
 * 数据来源优先级:
 *   1) capabilities (HuggingFace pipeline_tag csv) — BoCloudAI Platform 2026-05-03 起下发,
 *      与 SDK src/services/bocloud-auth/storage.ts 同口径
 *   2) endpointType / supportsImage — 旧字段 / 自定义 provider 用户手填
 *
 * 同一模型可能有多个标签(如 qwen3-vl 既能 image-text-to-text 也能 text-generation,
 * 但 text-generation 视为默认不打标),以下顺序决定渲染顺序:生图 → 生视频 → 图像
 * → 视频 → 语音(TTS) → 听写(STT) → 实时语音。
 */

import { modelSupportsImage, readModelCapabilities } from '../../shared/modelCapabilities';

export type CapabilityTone = 'imageGen' | 'videoGen' | 'image' | 'video' | 'tts' | 'stt' | 'audio';

export interface CapabilityLabel {
  /** UI 文案 */
  text: string;
  /** Tailwind 配色 — 由消费方根据 tone 选 className,集中管理避免散落 */
  tone: CapabilityTone;
}

/** tone → Tailwind classes(浅色 + 深色)。新增 tone 在此一处加。 */
export const CAPABILITY_TONE_CLASS: Record<CapabilityTone, string> = {
  imageGen: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  videoGen: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
  image:    'bg-violet-500/10 text-violet-600 dark:text-violet-400',
  video:    'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  tts:      'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  stt:      'bg-orange-500/10 text-orange-600 dark:text-orange-400',
  audio:    'bg-rose-500/10 text-rose-600 dark:text-rose-400',
};

interface ModelLike {
  capabilities?: string[] | string;
  supportsImage?: boolean;
  endpointType?: number;
}

/**
 * 给定模型,返回它应当展示的能力标签数组。返回空数组表示纯文本对话模型(无标签)。
 */
export function getModelCapabilityLabels(model: ModelLike): CapabilityLabel[] {
  const labels: CapabilityLabel[] = [];
  const caps = new Set(readModelCapabilities(model));

  // 生成类(text-to-X) — 优先,这类模型只能被工具调用,不能当对话模型
  if (caps.has('text-to-image') || caps.has('image-to-image') || (!caps.size && model.endpointType === 4)) {
    labels.push({ text: '生图', tone: 'imageGen' });
  }
  if (caps.has('text-to-video') || caps.has('image-to-video') || (!caps.size && model.endpointType === 7)) {
    labels.push({ text: '生视频', tone: 'videoGen' });
  }
  // 理解类(X-text-to-text) — 视觉/视频翻译模型,SDK visionRouting/videoRouting 用
  // 生图与识图可以同时声明，不能因生图标签排除识图。
  if (modelSupportsImage(model)) {
    labels.push({ text: '图像', tone: 'image' });
  }
  if (caps.has('video-text-to-text')) {
    labels.push({ text: '视频', tone: 'video' });
  }
  // 语音 — 当前 boclaw 主流程未消费,但发现 API 已透传,按 capability 展示
  if (caps.has('text-to-speech') || (!caps.size && model.endpointType === 5)) {
    labels.push({ text: 'TTS', tone: 'tts' });
  }
  if (caps.has('automatic-speech-recognition') || (!caps.size && model.endpointType === 6)) {
    labels.push({ text: 'STT', tone: 'stt' });
  }
  if (caps.has('audio-text-to-text')) {
    labels.push({ text: '语音', tone: 'audio' });
  }
  return labels;
}

/**
 * 是否为主对话模型(可在 Cowork 选择器里出现)。
 *
 * 三层判定:
 *   1) isPrimary 显式 false  → 非主(BoCloudAI Platform 副模型)
 *   2) capabilities 含 text-to-image / text-to-video / text-to-speech /
 *      automatic-speech-recognition 但不含 text-generation → 非主(纯生成/纯发现 API)
 *   3) 其余(含 isPrimary === true / undefined / 旧字段) → 主
 *
 * isPrimary undefined → 视为主,兼容本地配置 + 旧 platform + 自定义 provider。
 */
export { isPrimaryChatModel } from '../../shared/modelCapabilities';
